import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logoGala from '../assets/common/LogoGala.png';
import FormInput from '../components/FormInput';
import { apiCheckEmail, apiRegisterCoachRequest } from '../services/api';
import { PASSWORD_RULES_ATTR, PWD_RULES, validatePassword } from '../utils/password';
import { NAME_MAX, validateBirthDate, validateEmail, validateName } from '../utils/signupValidation';
import { todayISO } from '../utils/formDate';
import './auth.css';

const CLUB_MAX = 255;

const CoachSignUp = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: '', password: '', confirmPassword: '',
    firstName: '', lastName: '', club: '', birthDate: '',
    consentAccepted: false,
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((err) => ({ ...err, [field]: undefined }));
  };

  const pwdChecks = validatePassword(form.password);
  const allPwdValid = Object.values(pwdChecks).every(Boolean);
  const confirmMismatch = form.confirmPassword.length > 0 && form.confirmPassword !== form.password;

  const handleEmailBlur = async () => {
    const email = form.email.trim();
    if (!email) return;
    const formatError = validateEmail(email);
    if (formatError) {
      setErrors((err) => ({ ...err, email: formatError }));
      return;
    }
    try {
      const { available } = await apiCheckEmail(email);
      if (!available) setErrors((err) => ({ ...err, email: 'Cet email est déjà utilisé.' }));
    } catch {
      // erreur réseau — silencieux, revérifié au submit
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    const newErrors = {};
    const emailError = validateEmail(form.email);
    if (emailError) {
      newErrors.email = emailError;
    } else {
      try {
        const { available } = await apiCheckEmail(form.email.trim());
        if (!available) newErrors.email = 'Cet email est déjà utilisé.';
      } catch {
        // erreur réseau — le backend rejettera au besoin
      }
    }
    const firstNameError = validateName(form.firstName, 'Le prénom');
    const lastNameError = validateName(form.lastName, 'Le nom');
    const birthDateError = validateBirthDate(form.birthDate);
    if (firstNameError) newErrors.firstName = firstNameError;
    if (lastNameError) newErrors.lastName = lastNameError;
    if (birthDateError) newErrors.birthDate = birthDateError;
    if (form.club.trim().length > CLUB_MAX) newErrors.club = `Le nom du club ne peut dépasser ${CLUB_MAX} caractères.`;
    if (!allPwdValid) newErrors.password = true;
    if (form.confirmPassword !== form.password) newErrors.confirmPassword = true;
    if (!form.consentAccepted) newErrors.consent = 'Vous devez accepter les conditions générales et la politique de confidentialité.';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      await apiRegisterCoachRequest({
        mail: form.email.trim(),
        prenom: form.firstName.trim(),
        nom: form.lastName.trim(),
        date_naissance: form.birthDate, // input type=date → YYYY-MM-DD
        club: form.club.trim() || null,
        password: form.password,
        consent_accepted: form.consentAccepted,
      });
      setSubmitted(true);
    } catch (err) {
      setErrors({ submit: err.status === undefined
        ? 'Connexion impossible. Vérifiez votre réseau puis réessayez.'
        : err.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="page">
        <div className="container">
          <div className="signup-logo">
            <img src={logoGala} alt="CycleGala" className="signup-logo-image" />
          </div>
          <div style={{ fontSize: '48px', textAlign: 'center', margin: '8px 0' }}>📨</div>
          <h2 className="title">Demande envoyée</h2>
          <p className="subtitle">
            Un administrateur va examiner votre demande. Une fois validée, vous recevrez
            un code de vérification par email à l'adresse <strong>{form.email}</strong>.
          </p>
          <div className="button-group">
            <button
              className="btn btn--primary"
              onClick={() => navigate('/verification', { state: { email: form.email } })}
            >
              J'ai reçu mon code
            </button>
          </div>
          <div className="auth-link-row">
            <button type="button" className="auth-link-btn" onClick={() => navigate('/connexion')}>
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        <div className="signup-logo">
          <img src={logoGala} alt="CycleGala" className="signup-logo-image" />
        </div>

        <h2 className="signup-brand">
          <span className="signup-brand--cycle">Cycle</span>
          <span className="signup-brand--gala">Gala</span>
        </h2>
        <p className="subtitle">Créer un compte coach</p>

        <form onSubmit={handleSubmit} noValidate>
        <FormInput
          label="Email"
          type="email"
          name="email"
          autoComplete="username"
          inputMode="email"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="votre.email@exemple.com"
          value={form.email}
          error={errors.email}
          onChange={(e) => setField('email', e.target.value)}
          onBlur={handleEmailBlur}
        />
        {errors.email && <p className="signup-error">{errors.email}</p>}

        <FormInput
          label="Prénom"
          type="text"
          name="given-name"
          autoComplete="given-name"
          autoCapitalize="words"
          maxLength={NAME_MAX}
          placeholder="Prénom"
          value={form.firstName}
          error={errors.firstName}
          onChange={(e) => setField('firstName', e.target.value)}
        />
        {errors.firstName && <p className="signup-error">{errors.firstName}</p>}

        <FormInput
          label="Nom"
          type="text"
          name="family-name"
          autoComplete="family-name"
          autoCapitalize="words"
          maxLength={NAME_MAX}
          placeholder="Nom"
          value={form.lastName}
          error={errors.lastName}
          onChange={(e) => setField('lastName', e.target.value)}
        />
        {errors.lastName && <p className="signup-error">{errors.lastName}</p>}

        <FormInput
          label="Club (optionnel)"
          type="text"
          name="organization"
          autoComplete="organization"
          maxLength={CLUB_MAX}
          placeholder="Nom du club"
          value={form.club}
          error={errors.club}
          onChange={(e) => setField('club', e.target.value)}
        />
        {errors.club && <p className="signup-error">{errors.club}</p>}

        <FormInput
          label="Date de naissance"
          type="date"
          name="bday"
          autoComplete="bday"
          max={todayISO()}
          min="1920-01-01"
          value={form.birthDate}
          error={errors.birthDate}
          onChange={(e) => setField('birthDate', e.target.value)}
        />
        {errors.birthDate && <p className="signup-error">{errors.birthDate}</p>}

        <FormInput
          label="Mot de passe"
          type="password"
          name="new-password"
          autoComplete="new-password"
          passwordrules={PASSWORD_RULES_ATTR}
          placeholder="••••••••"
          value={form.password}
          error={errors.password}
          onChange={(e) => setField('password', e.target.value)}
        />
        {form.password.length > 0 && (
          <ul className="pwd-checklist">
            {PWD_RULES.map(({ key, label }) => (
              <li key={key} className={`pwd-checklist__item${pwdChecks[key] ? ' pwd-checklist__item--ok' : ''}`}>
                {pwdChecks[key] ? '✓' : '·'} {label}
              </li>
            ))}
          </ul>
        )}

        <FormInput
          label="Confirmer le mot de passe"
          type="password"
          name="confirm-password"
          autoComplete="new-password"
          placeholder="••••••••"
          value={form.confirmPassword}
          onChange={(e) => setField('confirmPassword', e.target.value)}
        />
        {(confirmMismatch || errors.confirmPassword) && (
          <p className="signup-error">Les mots de passe ne correspondent pas.</p>
        )}

        <label className="consent-checkbox">
          <input
            type="checkbox"
            id="consent-cgu-coach"
            name="consent_cgu"
            checked={form.consentAccepted}
            onChange={(e) => setField('consentAccepted', e.target.checked)}
          />
          <span>
            J'accepte la{' '}
            <button type="button" className="auth-link-btn" onClick={() => navigate('/legal/confidentialite')}>
              politique de confidentialité
            </button>{' '}
            et les{' '}
            <button type="button" className="auth-link-btn" onClick={() => navigate('/legal/conditions')}>
              conditions générales
            </button>
          </span>
        </label>
        {errors.consent && <p className="signup-error">{errors.consent}</p>}
        {errors.submit && <p className="signup-error">{errors.submit}</p>}

        <div className="button-group">
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? 'Envoi…' : 'Faire la demande'}
          </button>
        </div>
        </form>

        <div className="auth-link-row">
          <span>Vous êtes sportive ? </span>
          <button type="button" className="auth-link-btn" onClick={() => navigate('/inscription')}>
            Créer un compte sportive
          </button>
        </div>
      </div>
    </div>
  );
};

export default CoachSignUp;
